import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  SafTable,
  SafIcon,
  SafButton,
  SafAlert,
  SafDialog,
  SafText,
  SafOption,
  SafCombobox,
} from "@saffron/core-components/react";
import { useParams, useNavigate } from "react-router-dom";
import SafTextComp from "./Saftext";

// Utility function to get endpoint URL with operation parameter
const getEndpointUrl = (operation) => {
  return `${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}&operation=${operation}`;
};

const Integrations = () => {
  // Get the asset ID from URL params
  const { assetId: urlAssetId } = useParams();
  const navigate = useNavigate();

  // Use URL param as the primary source of truth
  const assetId = urlAssetId || "";

  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    target_asset_id: "",
    target_asset_name: "",
    direction: "",
    protocol: "",
    port: "",
  });
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [integrationsData, setIntegrationsData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const directionData = ["Inbound", "Outbound"];
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [, setIntegrationToDelete] = useState(null); // Fixed: was actorToDelete

  const buttonRef = useRef();
  const dialogRef = useRef();
  const deleteDialogRef = useRef();

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (dialog) {
      dialog.addEventListener("hide", closeDialog);

      return () => {
        dialog.removeEventListener("hide", closeDialog);
      };
    }
  }, []);

  // New useEffect for delete dialog
  useEffect(() => {
    const deleteDialog = deleteDialogRef?.current;
    if (deleteDialog) {
      deleteDialog.addEventListener("hide", closeDeleteDialog);

      return () => {
        deleteDialog.removeEventListener("hide", closeDeleteDialog);
      };
    }
  }, []);

  const closeDialog = () => {
    setIsOpen(false);
    // Reset edit mode when dialog closes
    setIsEditMode(false);
    setEditingIndex(null);
    setFormData({
      target_asset_id: "",
      target_asset_name: "",
      direction: "",
      protocol: "",
      port: "",
    });
    setErrors({});
  };

  // New function to close delete dialog
  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
    setIntegrationToDelete(null); // Fixed: was actorToDelete
  };

  const handleCancelClick = () => {
    dialogRef?.current?.hide();
  };

  // New function to handle delete dialog cancel
  const handleDeleteCancelClick = () => {
    deleteDialogRef?.current?.hide();
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    let newErrors = {};
    const validNumbers = /^-?\d+(\.\d+)?$/;

    if (formData.target_asset_id === "") {
      newErrors.target_asset_id = "Please enter target asset id";
    } else if (!formData.target_asset_id.match(validNumbers)) {
      newErrors.target_asset_id = "Please enter a valid number";
    }

    if (formData.target_asset_name === "") {
      newErrors.target_asset_name = "Please enter target asset name";
    }

    if (formData.direction === "") {
      newErrors.direction = "Please enter direction";
    }

    if (formData.protocol === "") {
      newErrors.protocol = "Please enter protocol";
    }

    if (formData.port === "") {
      newErrors.port = "Please enter port";
    } else if (!formData.port.match(validNumbers)) {
      newErrors.port = "Please enter a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to handle edit button click
  const handleEditClick = (integration, index) => {
    // Fixed: was (actor, index)
    setIsEditMode(true);
    setEditingIndex(index);
    setFormData({
      target_asset_id: integration.target_asset_id,
      target_asset_name: integration.target_asset_name,
      direction: integration.direction,
      protocol: integration.protocol,
      port: integration.port,
    });
    setIsOpen(true);
  };

  // Function to handle add button click
  const handleAddClick = () => {
    setIsEditMode(false);
    setEditingIndex(null);
    setFormData({
      target_asset_id: "",
      target_asset_name: "",
      direction: "",
      protocol: "",
      port: "",
    });
    setIsOpen(true);
  };

  // Function to handle delete button click
  const handleDeleteClick = (integration, index) => {
    setDeleteIndex(index);
    setIntegrationToDelete(integration); // Fixed: was setintegrationToDelete
    setIsDeleteDialogOpen(true);
  };

  // New function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteIndex === null) return;

    try {
      setIsLoading(true);

      const operation = "get";
      const getEndpoint = getEndpointUrl(operation);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Failed to check existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let responseData = await getResponse.json();
      console.log("Existing data response:", responseData);

      let documentData;

      // Handle if the response is an array (common with query results)
      if (Array.isArray(responseData) && responseData.length > 0) {
        documentData = responseData[0];
      }
      // Handle if the response is a single object
      else if (responseData && typeof responseData === "object") {
        documentData = responseData;
      } else {
        throw new Error("No existing document found to delete from");
      }

      // Initialize integrations array if it doesn't exist
      if (!documentData.integrations) {
        // Fixed: was integratiodocumentData.integrationsn
        documentData.integrations = [];
      }

      // Remove the integration at the specified index
      documentData.integrations.splice(deleteIndex, 1); // Fixed: was integratiodocumentData.integrationsn

      console.log("Document after deletion:", documentData);

      // Now update the document
      const insertOperation = "insert";
      const insertEndpoint = getEndpointUrl(insertOperation);

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: documentData,
          partitionKey: assetId,
        }),
      });

      if (!insertResponse.ok) {
        const errorText = await insertResponse.text();
        throw new Error(
          `Delete failed with status ${insertResponse.status}: ${errorText}`
        );
      }

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Integration was successfully deleted"); // Fixed: was "Actor was successfully deleted"
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);

      // Close delete dialog
      deleteDialogRef?.current?.hide();

      // Refresh data
      await getIntegrationsData(); // Fixed: was getActorsData()
    } catch (err) {
      console.error(err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to delete integration: " + err.message); // Fixed: was "Failed to delete actor"
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const getIntegrationsData = useCallback(async () => {
    if (!assetId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const operation = "get";
    const endpoint = getEndpointUrl(operation);

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
    const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!response.ok) {
        console.error(`Error: ${response.status} ${response.statusText}`);
        setIntegrationsData(null);
        return;
      }

      const data = await response.json();

      // Handle different response formats
      if (Array.isArray(data) && data.length > 0) {
        // If the API returns an array (common for Cosmos DB queries)
        setIntegrationsData(data[0]);
      } else if (
        data &&
        typeof data === "object" &&
        data.id === "architecture-details"
      ) {
        // If it's already a single object
        setIntegrationsData(data);
      } else {
        console.log("No valid architecture details found in response");
        setIntegrationsData(null);
      }
    } catch (error) {
      console.error("Error fetching integrations data:", error);
      setIntegrationsData(null);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  // Modified useEffect to prioritize URL parameter
  useEffect(() => {
    if (urlAssetId) {
      // If we have a URL param, use it to fetch data
      getIntegrationsData();
    } else if (localStorage.getItem("assetInfo")) {
      // If no URL param but we have localStorage data, redirect
      const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
      if (storedInfo.id) {
        navigate(`/${storedInfo.id}/integrations`, { replace: true });
      } else {
        // No valid data, go to home
        navigate("/home");
      }
    } else {
      // No data at all, go to home
      navigate("/home");
    }
  }, [urlAssetId, navigate, getIntegrationsData]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Please fill the required fields");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      return;
    }

    if (!assetId) {
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("No Asset ID found");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      return;
    }

    const integrationInfo = {
      target_asset_id: formData.target_asset_id,
      target_asset_name: formData.target_asset_name,
      direction: formData.direction,
      protocol: formData.protocol,
      port: formData.port,
    };

    try {
      setIsLoading(true);

      // First, check if architecture-details exists for this asset
      const operation = "get";
      const getEndpoint = getEndpointUrl(operation);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Failed to check existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      const existingDataResponse = await getResponse.json();

      let documentData;

      // Handle different response formats
      if (
        Array.isArray(existingDataResponse) &&
        existingDataResponse.length > 0
      ) {
        // If the API returns an array
        documentData = existingDataResponse[0];
      } else if (
        existingDataResponse &&
        typeof existingDataResponse === "object" &&
        existingDataResponse.id === "architecture-details"
      ) {
        // If it's already a single object
        documentData = existingDataResponse;
      } else {
        // If architecture-details doesn't exist, create it
        documentData = {
          id: "architecture-details",
          asset_id: assetId,
          arch_diags: [],
          adrs: [],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Initialize integrations array if it doesn't exist
      if (!documentData.integrations) {
        documentData.integrations = [];
      }

      // Handle edit vs add logic
      if (isEditMode && editingIndex !== null) {
        // Update existing integration
        documentData.integrations[editingIndex] = integrationInfo;
        console.log("Updated integration at index:", editingIndex);
      } else {
        // Add new integration
        documentData.integrations.push(integrationInfo);
        console.log("Added new integration");
      }

      // Now insert/update the document
      const insertOperation = "insert";
      const insertEndpoint = getEndpointUrl(insertOperation);

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: documentData,
          partitionKey: assetId,
        }),
      });

      if (!insertResponse.ok) {
        const errorText = await insertResponse.text();
        throw new Error(
          `Insert failed with status ${insertResponse.status}: ${errorText}`
        );
      }

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage(
        isEditMode
          ? "Integration was successfully updated"
          : "Integration was successfully added"
      );
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);

      setFormData({
        target_asset_id: "",
        target_asset_name: "",
        direction: "",
        protocol: "",
        port: "",
      });
      setErrors({});
      setIsEditMode(false);
      setEditingIndex(null);
      dialogRef?.current?.hide();
      getIntegrationsData(); // Refresh the data
    } catch (err) {
      console.error(err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage(
        `Failed to ${isEditMode ? "update" : "add"} integration: ` + err.message
      );
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dialog-header">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <SafText appearance="heading-2xl">Integrations</SafText>
      </div>
      <br />
      <div style={{ maxWidth: "1400px", margin: "0" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <SafText appearance="body-small-md">
            Please add all integrations / data flows that the asset uses to
            provide the service.
          </SafText>
          <SafButton
            size="small"
            ref={buttonRef}
            onClick={handleAddClick}
            id="button-dialog-trigger"
            aria-haspopup="dialog"
            aria-controls="button-dialog-overlay"
            aria-expanded={isOpen ? "true" : "false"}
          >
            <SafIcon
              slot="start"
              icon-name="plus"
              appearance="solid"
              presentation
            ></SafIcon>
            Add
          </SafButton>
        </div>
        <br />
        <SafTable
          alternating-rows="true"
          density="inherit"
          header-background="default"
          inline-borders="true"
          width="100%"
        >
          <table>
            <thead>
              <tr>
                <th style={{ width: "300px" }}>Target Asset Name</th>
                <th style={{ width: "300px" }}>Target Asset Id</th>
                <th style={{ width: "300px" }}>Direction</th>
                <th style={{ width: "300px" }}>Protocol</th>
                <th style={{ width: "300px" }}>Port</th>
                <th style={{ width: "300px" }}>Edit</th>
                <th style={{ width: "300px" }}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    {" "}
                    {/* Fixed: was colSpan="5" */}
                    Loading...
                  </td>
                </tr>
              ) : integrationsData &&
                integrationsData.integrations &&
                integrationsData.integrations.length > 0 ? (
                integrationsData.integrations.map((integration, index) => (
                  <tr key={index}>
                    <td>{integration.target_asset_name}</td>
                    <td>{integration.target_asset_id}</td>
                    <td>{integration.direction}</td>
                    <td>{integration.protocol}</td>
                    <td>{integration.port}</td>
                    <td>
                      <SafIcon
                        icon-name="pencil"
                        appearance="solid"
                        color="#404040"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEditClick(integration, index)}
                      />
                    </td>
                    <td>
                      <SafIcon
                        icon-name="trash-can"
                        appearance="solid"
                        color="#404040"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteClick(integration, index)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    {" "}
                    {/* Fixed: was colSpan="5" */}
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SafTable>
      </div>
      <div>
        <div>
          {openAlertOptions && (
            <SafAlert
              appearance={openAlertOptions[1]}
              alertType={openAlertOptions[0]}
              iconAriaLabel="icon aria label"
              closeAriaLabel="Close"
              onClose={handleOnClose}
            >
              {alertMessage}
            </SafAlert>
          )}
          <SafDialog
            id="button-dialog-overlay"
            modal={false}
            isAlert="false"
            isHeader="true"
            isFooter="true"
            noFocusTrap={false}
            dialogTitle={isEditMode ? "Edit Integration" : "Add Integration"}
            ref={dialogRef}
            hidden={!isOpen}
            triggerId="button-dialog-trigger"
            ZIndex="1000"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "24px",
              }}
            >
              <div>
                <SafTextComp
                  label="Target Asset ID"
                  name="target_asset_id"
                  value={formData.target_asset_id}
                  validationMessage={errors.target_asset_id}
                  onChange={handleInputChange}
                  onBlur={validateForm}
                />
                {errors.target_asset_id && (
                  <span style={{ color: "red" }}>{errors.target_asset_id}</span>
                )}
              </div>
              <div>
                <SafTextComp
                  label="Target Asset Name"
                  name="target_asset_name"
                  value={formData.target_asset_name}
                  validationMessage={errors.target_asset_name}
                  onChange={handleInputChange}
                  onBlur={validateForm}
                />
                {errors.target_asset_name && (
                  <span style={{ color: "red" }}>
                    {errors.target_asset_name}
                  </span>
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "24px",
              }}
            >
              <div>
                <SafCombobox
                  autocomplete="inline"
                  label="Direction"
                  name="direction"
                  value={formData.direction} // Added missing value prop
                  onChange={handleInputChange}
                  validationMessage={errors.direction}
                  onBlur={validateForm}
                  id="combobox-default"
                  a11y-aria-description="Start typing to narrow your search"
                >
                  {directionData.map((env, index) => (
                    <SafOption key={index}>{env}</SafOption>
                  ))}
                </SafCombobox>
                {errors.direction && (
                  <span style={{ color: "red" }}>{errors.direction}</span>
                )}
              </div>
              <div>
                <SafTextComp
                  label="Protocol"
                  name="protocol"
                  value={formData.protocol}
                  onChange={handleInputChange}
                  validationMessage={errors.protocol}
                  onBlur={validateForm}
                />
                {errors.protocol && (
                  <span style={{ color: "red" }}>{errors.protocol}</span>
                )}
              </div>
            </div>
            <div style={{ width: "207px" }}>
              <SafTextComp
                label="Port"
                name="port"
                value={formData.port}
                onChange={handleInputChange}
                validationMessage={errors.port}
                onBlur={validateForm}
              />
              {errors.port && (
                <span style={{ color: "red" }}>{errors.port}</span>
              )}
            </div>
            <div
              slot="footer"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <SafButton appearance="secondary" onClick={handleCancelClick}>
                Cancel
              </SafButton>
              <SafButton appearance="primary" onClick={handleSubmitForm}>
                {isEditMode ? "Update" : "Save"}
              </SafButton>
            </div>
          </SafDialog>

          {/* Delete Confirmation Dialog */}
          <SafDialog
            id="delete-dialog-overlay"
            modal={true}
            isAlert="true"
            isHeader="true"
            isFooter="true"
            noFocusTrap={false}
            dialogTitle="Delete Integration" // Fixed: was "Delete Actor"
            ref={deleteDialogRef}
            hidden={!isDeleteDialogOpen}
            ZIndex="1"
          >
            <div style={{ padding: "20px 0" }}>
              <SafText appearance="body-md">
                Are you sure you want to delete this integration?{" "}
                {/* Fixed: was "actor" */}
              </SafText>
              <br />
            </div>

            <div
              slot="footer"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <SafButton
                appearance="secondary"
                onClick={handleDeleteCancelClick}
                type="button"
              >
                No
              </SafButton>
              <SafButton
                appearance="danger"
                onClick={handleDeleteConfirm}
                type="button"
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}{" "}
                {/* Fixed: was just "Delete" */}
              </SafButton>
            </div>
          </SafDialog>
        </div>
      </div>
    </div>
  );
};

export default Integrations;
